import { type AnalyticsEvent, trackAnalyticsEvent } from '@autone/analytics';
import { useAutoneTranslation } from '@autone/translations';
import { fISOToYearMonthDay } from '@autone/utils';
import { type PopoverProps } from '@radix-ui/react-popover';
import { CalendarIcon } from 'lucide-react';
import { forwardRef } from 'react';
import { type DateRange, type Matcher } from 'react-day-picker';

import { getDisplayedDateRange } from '../utils/getDisplayedDateRange';

import { Button, type ButtonProps } from './Button';
import { DatePickerCalendar } from './DatePickerCalendar';
import { inputVariants } from './inputs/inputVariants';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import { cn } from './utils';

type DateRangePickerButtonProps = {
  value: DateRange | undefined;
} & Omit<ButtonProps, 'value'>;

export const DateRangePickerButton = forwardRef<
  HTMLButtonElement,
  DateRangePickerButtonProps
>(({ value, placeholder, className, ...props }, ref) => {
  const { lang } = useAutoneTranslation();
  return (
    <Button
      id="date"
      variant="outlined"
      color="secondary"
      disableRipple
      className={cn(
        inputVariants({ variant: 'default' }),
        'focus-within:outline focus-within:outline-2 focus-within:outline-primary-100 focus-within:hover:border-grey-10',
        'flex justify-between gap-10 font-normal px-4',
        className,
      )}
      ref={ref}
      {...props}
    >
      {getDisplayedDateRange({
        value,
        placeholder,
        lang,
      })}
      <CalendarIcon size={18} />
    </Button>
  );
});

type DateRangePickerProps = {
  value: DateRange | undefined;
  placeholder: string;
  onChange: (date?: DateRange) => void;
  analyticsEvent?: AnalyticsEvent;
  datePickerDisabled?: Matcher | Matcher[] | undefined;
} & PopoverProps &
  Omit<ButtonProps, 'value' | 'onChange'>;

export const DateRangePicker = forwardRef<
  HTMLButtonElement,
  DateRangePickerProps
>(
  (
    {
      value,
      placeholder,
      disabled,
      onChange,
      className,
      analyticsEvent,
      datePickerDisabled,
      ...props
    },
    ref,
  ) => {
    const { common } = useAutoneTranslation();

    const handleChange = (date?: DateRange) => {
      onChange(date);
      if (analyticsEvent) {
        trackAnalyticsEvent({
          ...analyticsEvent,
          properties: {
            ...analyticsEvent.properties,
            start_date: fISOToYearMonthDay(date?.from ?? ''),
            end_date: fISOToYearMonthDay(date?.to ?? ''),
          },
        });
      }
    };

    return (
      <Popover {...props}>
        <PopoverTrigger asChild>
          <DateRangePickerButton
            ref={ref}
            value={value}
            placeholder={placeholder}
            disabled={disabled}
            className={className}
          />
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3" align="start">
          <DatePickerCalendar
            disabled={datePickerDisabled}
            autoFocus
            mode="range"
            selected={value}
            onSelect={handleChange}
            numberOfMonths={2}
          />
          <Button
            variant="outlined"
            color="secondary"
            disabled={!value?.from}
            onClick={() => handleChange(undefined)}
          >
            {common('filters.clear-all-selections')}
          </Button>
        </PopoverContent>
      </Popover>
    );
  },
);
